import { BrowserSupport } from '@tableau/browser-support';
import * as _ from 'underscore';

import { getAppStore } from '../../app/AppStore';
import { TypeaheadFilter } from '../../app/content/TypeaheadFilter';
import { IMainScope } from '../../app/IMainScope';
import { selectSpaceInfo } from '../../app/selectors/SpaceInfoSelectors';
import { isSearchSpace, isSomeKindaSearchSpace } from '../../app/state/SpaceInfo';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Order } from '../helpers/Order';
import { ContentFilterKeyName, ContentFiltersKeys } from '../models/ContentFilters';
import { DatasourceContentType, ViewContentType, WorkbookContentType } from '../models/ContentTypes';
import { ITypeaheadSource } from '../models/ITypeaheadSource';
import { OrderKeys } from '../models/OrderKeys';
import { Module } from '../Module';
import { FieldValuesQuery } from '../resources/FieldValuesQuery';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IFilterDefinition, ITypeaheadFilterDefinition } from '../services/FilterPanelService';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../FeatureFlags';

export interface IFilterControl {
  definition: IFilterDefinition<any>;
  ready: boolean;
  value: any;
  clear?: () => void;
}

export interface IFilterControls {
  clearAllFilters?: () => any;
}

export interface FilterPanelTypeaheadScope {
  filterFieldValues: (filterField: string) => ITypeaheadSource;
  getFieldValueQuery: (filterField: string) => FieldValuesQuery;
}

export interface IFilterPanelContainerScope extends IMainScope {
  filterDefinitions?: IFilterDefinition<any>[];
  filterControls: IFilterControls;
  hideSearch?: boolean;
}

export interface IFilterPanelScope extends IFilterPanelContainerScope, FilterPanelTypeaheadScope {
  contentType: string;
  searchText: string;
  filters: IFilterControl[];
  anyFiltersActive: boolean;

  submitSearch: (searchText: string, $event?: JQueryEventObject) => void;
  typeaheadFilterPropsByFilterName: _.Dictionary<TypeaheadFilter.Props>;

  anyGeneralFilters: () => boolean;
  generalFilter: (filter: IFilterControl) => boolean;
  generalCheckbox: (filter: IFilterControl) => boolean;
  anyDatasourceFilters: () => boolean;
  datasourceFilter: (filter: IFilterControl) => boolean;
  datasourceCheckbox: (filter: IFilterControl) => boolean;
  anyProjectFilters: () => boolean;
  projectCheckbox: (filter: IFilterControl) => boolean;
  anyDataAccelerationFilters: (filter: IFilterControl) => boolean;
  dataAccelerationFilter: (filter: IFilterControl) => boolean;
}

type UnwatchMethod = () => void;

export class FilterPanelCtrl {

  public static $inject = ['$scope', '$location'];
  private unwatchFilterValuesMethods: UnwatchMethod[];

  /**
   * Returns true if any filters are active from the URL.
   * @param urlParamsKeys - A list of current param keys in url
   * @param inSearchSpace - true if we are currently in the search space
   * @returns {boolean}
   */
  public static anyFiltersActive(urlParamsKeys: string[], inSearchSpace: boolean, filtersToRemove: ContentFilterKeyName[]): boolean {
    return _.some(urlParamsKeys, (key: string) => {

      // Account for the search space scenario in which the search space is the only filter set. In this case we don't count it as an active filter
      if (inSearchSpace && key === ContentFiltersKeys.search) {
        filtersToRemove.push(ContentFiltersKeys.search);
      }
      let filtersToKeep = _.difference(_.values(ContentFiltersKeys), filtersToRemove);
      // we do not want contentType to be controlled by the clear-all-filter button
      return _.contains(filtersToKeep, key);
    });
  }

  constructor($scope: IFilterPanelScope, private $location: ng.ILocationService) {
    $scope.filters = this.makeFilters($scope.filterDefinitions);

    $scope.$watch('filterDefinitions', (newFilterDefinitions: IFilterDefinition<any>[]) => {
      $scope.filters = this.makeFilters(newFilterDefinitions);
      this.updateFilterModels($scope, $location, updateModelFromURL);
    });

    const isGeneral = (filter: IFilterControl) => filter.definition.category === 'general';
    const isDatasource = (filter: IFilterControl) => filter.definition.category === 'datasource';
    const isProject = (filter: IFilterControl) => filter.definition.category === 'project';
    const isCheckbox = (filter: IFilterControl) => filter.definition.type === 'boolean';
    const isDataAcceleration = (filter: IFilterControl) => filter.definition.category === 'dataAcceleration';

    $scope.generalFilter = (filter: IFilterControl) => isGeneral(filter) && !isCheckbox(filter);
    $scope.generalCheckbox = (filter: IFilterControl) => isGeneral(filter) && isCheckbox(filter);
    $scope.datasourceFilter = (filter: IFilterControl) => isDatasource(filter) && !isCheckbox(filter);
    $scope.datasourceCheckbox = (filter: IFilterControl) => isDatasource(filter) && isCheckbox(filter);
    $scope.projectCheckbox = (filter: IFilterControl) => isProject(filter) && isCheckbox(filter);
    $scope.dataAccelerationFilter = (filter: IFilterControl) => isDataAcceleration(filter) && !isCheckbox(filter);

    $scope.anyGeneralFilters = () => _.some($scope.filters, isGeneral);
    $scope.anyDatasourceFilters = () => _.some($scope.filters, isDatasource);
    $scope.anyProjectFilters = () => _.some($scope.filters, isProject);
    $scope.anyDataAccelerationFilters = () => FeatureFlags.workbookAccelerationUIEnabled
                                                && _.some($scope.filters, isDataAcceleration);

    $scope.filterControls.clearAllFilters = () => {
      const urlParams = $location.search();
      const inSearchSpace = isSomeKindaSearchSpace(selectSpaceInfo(getAppStore().getState()));
      _.each(_.keys(urlParams), (key: string) => {
        if (_.has(ContentFiltersKeys, key)) {
          if (inSearchSpace && key === ContentFiltersKeys.search) {
            return;
          }
          $location.search(key, null);
        }
      });
      _.each($scope.filters, (filterControl: IFilterControl) => {
        if (filterControl.clear) {
          filterControl.clear();
        }
      });

      // A text search filter automatically orders the results by relevance;
      // clear that extra sort order when clearing the filters
      if ($scope.contentType === WorkbookContentType.name || $scope.contentType === ViewContentType.name ||
        $scope.contentType === DatasourceContentType.name) {
        this.removeRelevanceFromOrder();
      }
    };

    $scope.submitSearch = (searchText: string, $event?: JQueryEventObject) => {
      $location.search(ContentFiltersKeys.search, searchText || null);

      // A text search filter automatically orders the results by relevance
      if ($scope.contentType === WorkbookContentType.name || $scope.contentType === ViewContentType.name ||
        $scope.contentType === DatasourceContentType.name) {
        if (searchText) {
          $location.search(UrlParamKeys.Order, Order.serialize([Order.desc(OrderKeys.relevance)]));
        } else {
          this.removeRelevanceFromOrder();
        }
      }

      if (BrowserSupport.IsTouch() && $event) {
        $($event.target).trigger('blur');
      }
    };

    $scope.typeaheadFilterPropsByFilterName = this.makeTypeaheadFilterProps($scope.filters, $scope);

    this.unwatchFilterValuesMethods = this.watchFilterValueChanges($scope, $location);

    let filtersToRemove: ContentFilterKeyName[] = [];
    if (getSessionManagementServiceInstance().isLimitedUserVisibilityEnforced()) {
      filtersToRemove.push(ContentFiltersKeys.owner);
    }

    function updateModelFromURL() {
      const urlParams = $location.search();
      const urlParamsKeys = _.keys(urlParams);
      const inSearchSpace = isSomeKindaSearchSpace(selectSpaceInfo(getAppStore().getState()));
      $scope.anyFiltersActive = FilterPanelCtrl.anyFiltersActive(urlParamsKeys, inSearchSpace, filtersToRemove);

      $scope.searchText = urlParams.search || '';

      _.each($scope.filters, (filterControl: IFilterControl) => {
        filterControl.ready = false;
        filterControl.definition.valueFromParams(urlParams).then((value: any) => {
          filterControl.ready = true;
          filterControl.value = value;
        });
      });
    }

    updateModelFromURL();
    $scope.$on('$locationChangeSuccess', () => {
      updateModelFromURL();
    });
  }

  private updateFilterModels($scope: IFilterPanelScope, $location: ng.ILocationService,
      updateModelFromURL: () => void) {
    this.resetFilterWatchers($scope, $location);
    updateModelFromURL();
    $scope.typeaheadFilterPropsByFilterName = this.makeTypeaheadFilterProps($scope.filters, $scope);
  }

  private resetFilterWatchers($scope: IFilterPanelScope, $location: ng.ILocationService) {
    while (this.unwatchFilterValuesMethods.length > 0) {
      const unwatch = this.unwatchFilterValuesMethods.pop();
      unwatch();
    }
    this.unwatchFilterValuesMethods = this.watchFilterValueChanges($scope, $location);
  }

  private watchFilterValueChanges(filterScope: IFilterPanelScope, $location: ng.ILocationService): UnwatchMethod[] {
    const unwatchMethods: UnwatchMethod[] = [];

    // Update location when filter selection changes
    _.each(filterScope.filters, (filterControl: IFilterControl) => {
      unwatchMethods.push(filterScope.$watch(() => filterControl.value, () => {
        if (filterControl.ready) {
          const params = filterControl.definition.valueToParams(filterControl.value);
          _.each(params, (value: any, key: string) => {
            $location.search(key, value);
          });
        }
      }));
    });

    return unwatchMethods;
  }

  private makeFilters(filterDefinitions: IFilterDefinition<any>[]): IFilterControl[] {
    return _.map(filterDefinitions, (defn: IFilterDefinition<any>): IFilterControl => {
      return { definition: defn, ready: false, value: undefined };
    });
  }

  private makeTypeaheadFilterProps(filters: IFilterControl[], scope: FilterPanelTypeaheadScope): _.Dictionary<TypeaheadFilter.Props> {
      function makeProps(filter: IFilterControl) {
        return {
          filter: filter,
          getFieldValueQuery: () => scope.getFieldValueQuery((filter.definition as ITypeaheadFilterDefinition).field)
        };
      }

      const propsArray = filters.filter(f => f.definition.type === 'typeahead').map(makeProps);
      return _.indexBy(propsArray, props => props.filter.definition.name);
  }

  private removeRelevanceFromOrder() {
    const orderValue = this.$location.search()[UrlParamKeys.Order];
    if (orderValue) {
      // The user may have added other order params, so we only remove relevance.
      let order: ReadonlyArray<IOrderClause> = Order.fromInput(orderValue);
      order = Order.withoutField(order, OrderKeys.relevance);

      const serializedOrder = Order.serialize(order);
      // we want to remove the search query parameter if there is none, passing 'null' does that.
      this.$location.search(UrlParamKeys.Order, serializedOrder === ''? null : serializedOrder);
    }
  }

}

Module.instance().controller('FilterPanelCtrl', FilterPanelCtrl);
